import { useMemo } from 'react';
import { RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { cn } from '@/lib/utils/cn';
import { useAuthStore } from '@/lib/auth/store';
import {
  useAdminLiveCampaigns,
  useCampaignReportDownload,
  useFetchVoiceBase,
} from '../api';
import { buildCampaignColumns, DEFAULT_VISIBLE_COLUMNS } from '../columns';
import type { CampaignRow } from '../types';

export function AdminLiveCampaignsPage() {
  const query = useAdminLiveCampaigns();
  const rows = query.data ?? [];
  // SADMIN / sub-super-admin see the Parent Username column; resellers
  // (ROLE_ADMIN) don't — mirrors the `hidden: reseller` flag on the legacy
  // ListSuperadminVoiceCampaign column.
  const showParentUsername = useAuthStore((s) => s.isSubOrSuperAdmin());
  const fetchBase = useFetchVoiceBase();

  const handleViewBase = (row: CampaignRow) => {
    fetchBase.mutate(row.campaignId, {
      onSuccess: (res) => {
        const url = Array.isArray(res) ? res[0]?.baseUrl : undefined;
        if (!url) {
          toast.error('Base file URL not found.');
          return;
        }
        window.open(url, '_blank', 'noopener,noreferrer');
      },
      onError: () => toast.error('Error opening base file.'),
    });
  };

  const reportDownload = useCampaignReportDownload();
  const columns = useMemo(
    () =>
      buildCampaignColumns({
        showOwner: true,
        showParentUsername,
        onViewBase: showParentUsername ? handleViewBase : undefined,
        onDownloadReport: reportDownload.download,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [showParentUsername, reportDownload.download],
  );

  // Admin views show owner columns by default.
  const visibility = {
    ...DEFAULT_VISIBLE_COLUMNS,
    userId: true,
    username: true,
    parentUsername: showParentUsername,
  };

  return (
    <FillPage>
      <PageHeader
        title="All Campaigns"
        description="Every live campaign across the users you manage. Refreshes every 30 seconds."
        actions={
          <Button
            variant="outline"
            size="icon"
            onClick={() => query.refetch()}
            disabled={query.isFetching}
            aria-label="Refresh"
            title="Refresh"
          >
            <RefreshCw className={cn('h-4 w-4', query.isFetching && 'animate-spin')} />
          </Button>
        }
      />

      <DataTable<CampaignRow, unknown>
        tableId="campaigns:voice:admin-live:v1"
        columns={columns}
        data={rows}
        isLoading={query.isLoading}
        searchPlaceholder="Search campaigns…"
        emptyTitle="No live campaigns"
        emptyDescription="When any of your users start a campaign, it appears here."
        getRowId={(row) => String(row.campaignId)}
        initialColumnPinning={{ right: ['actions'] }}
        initialColumnVisibility={visibility}
        exportFilename="voice-campaigns-admin-live.csv"
        pageSize={10}
      />
    </FillPage>
  );
}
